import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preferência de UNIDADE DE EXIBIÇÃO do sistema (Configurações → Unidades).
//
// Forma estrita das camadas (ADR-2026-10-25, INV-LAYER-READPATH-001,
// INV-UI-TRAIL-001): este contexto só LÊ e SALVA a preferência — não existe
// mais tabela de conversão no frontend. O backend aplica a preferência e os
// endpoints devolvem valor + unidade prontos (`*_display`, `display.*`,
// `computed.*`); a tela só renderiza.
const SPEED_OPTIONS = {
  kn: { label: 'kn', name: 'Nos nauticos' },
  kmh: { label: 'km/h', name: 'Quilometros por hora' },
  mph: { label: 'mph', name: 'Milhas por hora' },
};

const DISTANCE_OPTIONS = {
  nm: { label: 'nm', name: 'Milhas nauticas' },
  km: { label: 'km', name: 'Quilometros' },
  mi: { label: 'mi', name: 'Milhas' },
};

const TEMPERATURE_OPTIONS = {
  c: { label: '°C', name: 'Celsius' },
  f: { label: '°F', name: 'Fahrenheit' },
};

const PRESSURE_OPTIONS = {
  bar: { label: 'bar', name: 'Bar' },
  psi: { label: 'psi', name: 'PSI' },
  kpa: { label: 'kPa', name: 'kPa' },
  hpa: { label: 'hPa', name: 'hPa' },
};

const DEPTH_OPTIONS = {
  m: { label: 'm', name: 'Metros' },
  ft: { label: 'ft', name: 'Pes' },
  fathoms: { label: 'fathoms', name: 'Bracas' },
};

const VOLUME_OPTIONS = {
  l: { label: 'L', name: 'Litros' },
  gal: { label: 'gal', name: 'Galoes' },
};

const FLOW_OPTIONS = {
  lh: { label: 'L/h', name: 'Litros por hora' },
  gph: { label: 'gal/h', name: 'Galoes por hora' },
};

const RUNTIME_OPTIONS = {
  h: { label: 'h', name: 'Horas' },
  min: { label: 'min', name: 'Minutos' },
};

const DEFAULT_UNITS = {
  speed_unit: 'kmh',
  distance_unit: 'km',
  temperature_unit: 'c',
  pressure_unit: 'bar',
  depth_unit: 'm',
  volume_unit: 'l',
  flow_unit: 'lh',
  runtime_unit: 'h',
};

const toOptions = (table) => Object.entries(table).map(([value, config]) => ({
  value,
  label: config.name,
  unit: config.label,
}));

const UnitsContext = createContext(null);

export const useUnits = () => {
  const context = useContext(UnitsContext);
  if (!context) {
    throw new Error('useUnits must be used within a UnitsProvider');
  }
  return context;
};

export const UnitsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_UNITS);
  const [loading, setLoading] = useState(true);

  const applySettings = useCallback((next) => {
    setSettings({ ...DEFAULT_UNITS, ...(next || {}) });
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/units`);
      applySettings(res.data || {});
    } catch (error) {
      console.log('Using default units settings');
      applySettings(DEFAULT_UNITS);
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async (nextSettingsOrSpeed, nextDistanceUnit) => {
    try {
      const payload = typeof nextSettingsOrSpeed === 'object'
        ? { ...settings, ...nextSettingsOrSpeed }
        : { ...settings, speed_unit: nextSettingsOrSpeed, distance_unit: nextDistanceUnit };

      await axios.put(`${API}/settings/units`, payload);
      applySettings(payload);
      return true;
    } catch (error) {
      console.error('Error saving units settings:', error);
      return false;
    }
  };

  const value = {
    speedUnit: settings.speed_unit,
    distanceUnit: settings.distance_unit,
    temperatureUnit: settings.temperature_unit,
    pressureUnit: settings.pressure_unit,
    depthUnit: settings.depth_unit,
    volumeUnit: settings.volume_unit,
    flowUnit: settings.flow_unit,
    runtimeUnit: settings.runtime_unit,
    loading,

    saveSettings,

    speedOptions: toOptions(SPEED_OPTIONS),
    distanceOptions: toOptions(DISTANCE_OPTIONS),
    temperatureOptions: toOptions(TEMPERATURE_OPTIONS),
    pressureOptions: toOptions(PRESSURE_OPTIONS),
    depthOptions: toOptions(DEPTH_OPTIONS),
    volumeOptions: toOptions(VOLUME_OPTIONS),
    flowOptions: toOptions(FLOW_OPTIONS),
    runtimeOptions: toOptions(RUNTIME_OPTIONS),
  };

  return (
    <UnitsContext.Provider value={value}>
      {children}
    </UnitsContext.Provider>
  );
};
